import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Location } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { IAppData } from '../models/app-data.model';
import { map, shareReplay } from 'rxjs/operators';
import { AppDetailsServiceData } from './app-details.service.data.service';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

@Injectable({
  providedIn: 'root',
})
export class AppDetailsService {
  endpoint = '/api/AppDetails/';
  private cachedAppData$: Observable<IAppData> | null = null;

  constructor(
    private http: HttpClient,
    private appDetailsServiceData: AppDetailsServiceData,
    private location: Location,
    @Inject(DOCUMENT) private document: Document
  ) { }

  getAppDetailsData(): Observable<IAppData> {
    // If data is already initialized, return cached data immediately
    if (this.appDetailsServiceData.isInitialised()) {
      const cachedData: IAppData = {
        version: this.appDetailsServiceData.getVersion(),
        dbConnected: this.appDetailsServiceData.isDbConnected()
      };
      return of(cachedData);
    }

    // If we have a cached Observable (request in flight), return it to share the same request
    if (this.cachedAppData$) {
      return this.cachedAppData$;
    }

    // Make HTTP request and cache the Observable result using shareReplay
    // This ensures all subscribers get the same result and only one HTTP request is made
    const url = this.endpoint + 'AppData';
    this.cachedAppData$ = this.http.get<IAppData>(url, httpOptions).pipe(
      map((resultAppData: IAppData) => {
        this.appDetailsServiceData.init(resultAppData);
        return resultAppData;
      }),
      shareReplay(1) // Cache the last emitted value and share it with all subscribers
    );

    return this.cachedAppData$;
  }

  isProduction(): boolean {
    if (this.appDetailsServiceData.getVersion().includes('P')) {
      return true;
    }

    return false;
  }

  shouldShowOAuthWarning(): boolean {
    const hostname = this.document.location.hostname;
    const protocol = this.document.location.protocol;
    return hostname === 'demo.sealadder' && protocol === 'https:';
  }

  redirectToLocalhost(): void {
    const protocol = this.document.location.protocol;
    const port = this.document.location.port;
    const pathname = this.location.path();
    const search = this.document.location.search;
    const hash = this.document.location.hash;
    const localhostUrl = `${protocol}//localhost:${port || '5001'}${pathname}${search}${hash}`;
    // External redirect (different hostname) requires document.location.href - this is acceptable per coding guidelines
    this.document.location.href = localhostUrl;
  }
}
